import React from 'react';
import {
  Box, Card, CardContent, IconButton, Typography,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { LessonMeta } from '../../Lesson';

export interface LessonCardProps {
  lesson: LessonMeta;
  bookmarked: boolean;
  onOpen: (slug: string) => void;
  onToggleBookmark: (slug: string, title: string) => void;
}

export default function LessonCard({
  lesson, bookmarked, onOpen, onToggleBookmark,
}: LessonCardProps) {
  return (
    <Card
      onClick={() => onOpen(lesson.slug)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: '#fff',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
      }}
    >
      {/* The prototype hard-codes a 184px band, which frames the art correctly only
          at its own 345px card width (1140px container, auto-fill minmax(300px,1fr)).
          Our hub container is wider — 1250px with three fixed columns, so ~401px
          cards — and a 401x184 band is 2.18:1 against art that is about 1.80:1, so
          `cover` was cropping ~21% of the image height instead of ~4%.

          Lock the design's 345:184 ratio instead of its pixel height, so the framing
          Bailey approved holds at every column width rather than only at hers. */}
      <Box sx={{
        position: 'relative', aspectRatio: '345 / 184', flexShrink: 0, bgcolor: '#ECEAF3',
      }}
      >
        {lesson.cardImage && (
          <Box
            component="img"
            src={lesson.cardImage}
            alt=""
            sx={{
              // Inset rather than height:100% so the ratio above drives the box.
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              // Centred, matching the prototype. We previously pinned this to `top`,
              // which took the whole crop off the bottom and cut off feet and legs.
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}
        <IconButton
          // Must not open the lesson — QA 2.2.
          onClick={(e) => { e.stopPropagation(); onToggleBookmark(lesson.slug, lesson.title); }}
          aria-label={bookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 36,
            height: 36,
            bgcolor: 'rgba(255,255,255,0.92)',
            boxShadow: 1,
            color: bookmarked ? 'rgba(55, 0, 179, 1)' : 'rgba(0,0,0,0.6)',
            '&:hover': { bgcolor: '#fff' },
          }}
        >
          {bookmarked ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
        </IconButton>
      </Box>
      <CardContent sx={{
        flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, p: '20px 22px 22px', '&:last-child': { pb: '22px' },
      }}
      >
        {/* Titles wrap rather than truncate — several run past one line. */}
        <Typography sx={{
          fontFamily: 'Rasa, serif',
          fontSize: '19px',
          fontWeight: 600,
          lineHeight: 1.2,
          color: '#23036A',
        }}
        >
          {lesson.title}
        </Typography>
        <Typography sx={{
          fontSize: '15px',
          lineHeight: 1.55,
          color: 'rgba(0,0,0,0.62)',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
        }}
        >
          {lesson.cardSummary}
        </Typography>
      </CardContent>
    </Card>
  );
}
